import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle2,
  Circle,
  Plus,
  FileText,
  ExternalLink,
  Clock,
  Edit,
  Trash2,
} from 'lucide-react';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge, statusBadgeVariants } from '../../components/ui/Badge';
import { LoadingScreen } from '../../components/ui/Spinner';
import { Modal, ModalActions } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { Select } from '../../components/ui/Select';
import { useAuth } from '../../contexts/AuthContext';
import {
  getProject,
  getProjectUpdates,
  getDeliverables,
  updateProject,
  createProjectUpdate,
  createDeliverable,
  updateDeliverable,
} from '../../lib/supabase/queries';
import type { Project, ProjectUpdate, Deliverable, ProjectStatus } from '../../types';

const statusOptions = [
  { value: 'discovery', label: 'Discovery' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'review', label: 'Review' },
  { value: 'complete', label: 'Complete' },
];

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const [project, setProject] = useState<Project | null>(null);
  const [updates, setUpdates] = useState<ProjectUpdate[]>([]);
  const [deliverables, setDeliverables] = useState<Deliverable[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Modals
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeliverableModal, setShowDeliverableModal] = useState(false);

  // Form states
  const [newStatus, setNewStatus] = useState<ProjectStatus>('discovery');
  const [newUpdate, setNewUpdate] = useState({ content: '', is_visible_to_client: true });
  const [newDeliverable, setNewDeliverable] = useState({ title: '', file_url: '' });

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    async function fetchData() {
      if (!id) {
        navigate('/dashboard/projects');
        return;
      }

      setIsLoading(true);
      try {
        const [projectData, updatesData, deliverablesData] = await Promise.all([
          getProject(id),
          getProjectUpdates(id, isAdmin),
          getDeliverables(id),
        ]);

        if (!projectData) {
          navigate('/dashboard/projects');
          return;
        }

        setProject(projectData);
        setUpdates(updatesData);
        setDeliverables(deliverablesData);
        setNewStatus(projectData.status);
      } catch (error) {
        console.error('Error fetching project:', error);
        navigate('/dashboard/projects');
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, [id, navigate, isAdmin]);

  const handleUpdateStatus = async () => {
    if (!project) return;
    setIsSaving(true);
    try {
      const updated = await updateProject(project.id, { status: newStatus });
      if (updated) {
        setProject(updated);
        setShowStatusModal(false);
      }
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddUpdate = async () => {
    if (!project || !newUpdate.content) return;
    setIsSaving(true);
    try {
      const update = await createProjectUpdate({
        project_id: project.id,
        content: newUpdate.content,
        is_visible_to_client: newUpdate.is_visible_to_client,
      });
      if (update) {
        setUpdates([update, ...updates]);
        setShowUpdateModal(false);
        setNewUpdate({ content: '', is_visible_to_client: true });
      }
    } catch (error) {
      console.error('Error adding update:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddDeliverable = async () => {
    if (!project || !newDeliverable.title) return;
    setIsSaving(true);
    try {
      const deliverable = await createDeliverable({
        project_id: project.id,
        title: newDeliverable.title,
        file_url: newDeliverable.file_url || null,
        is_complete: false,
      });
      if (deliverable) {
        setDeliverables([...deliverables, deliverable]);
        setShowDeliverableModal(false);
        setNewDeliverable({ title: '', file_url: '' });
      }
    } catch (error) {
      console.error('Error adding deliverable:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleDeliverable = async (deliverable: Deliverable) => {
    try {
      await updateDeliverable(deliverable.id, {
        is_complete: !deliverable.is_complete,
      });
      setDeliverables(
        deliverables.map((d) =>
          d.id === deliverable.id ? { ...d, is_complete: !d.is_complete } : d
        )
      );
    } catch (error) {
      console.error('Error toggling deliverable:', error);
    }
  };

  if (isLoading) {
    return <LoadingScreen message="Loading project..." />;
  }

  if (!project) {
    return null;
  }

  const completedDeliverables = deliverables.filter((d) => d.is_complete).length;
  const totalDeliverables = deliverables.length;
  const progress =
    totalDeliverables > 0
      ? Math.round((completedDeliverables / totalDeliverables) * 100)
      : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link
          to="/dashboard/projects"
          className="inline-flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-amber dark:hover:text-amber mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Projects
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl md:text-3xl font-bold text-obsidian dark:text-signal">
                {project.title}
              </h1>
              <Badge variant={statusBadgeVariants[project.status] || 'default'} size="md">
                {project.status.replace('_', ' ')}
              </Badge>
            </div>
            {project.description && (
              <p className="text-slate-600 dark:text-slate-400 max-w-2xl">
                {project.description}
              </p>
            )}
            {isAdmin && project.client && (
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                Client: {project.client.company_name || 'Unknown'}
              </p>
            )}
          </div>
          {isAdmin && (
            <Button
              variant="outline"
              leftIcon={<Edit className="w-4 h-4" />}
              onClick={() => setShowStatusModal(true)}
            >
              Update Status
            </Button>
          )}
        </div>
      </div>

      {/* Progress */}
      {totalDeliverables > 0 && (
        <Card variant="bordered" padding="md">
          <CardContent>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Progress
              </span>
              <span className="text-sm text-slate-500 dark:text-slate-400">
                {completedDeliverables} of {totalDeliverables} deliverables
              </span>
            </div>
            <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2.5">
              <div
                className="bg-amber h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Deliverables */}
        <div className="lg:col-span-2">
          <Card variant="bordered" padding="none">
            <CardHeader className="p-6 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <CardTitle>Deliverables</CardTitle>
                {isAdmin && (
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<Plus className="w-4 h-4" />}
                    onClick={() => setShowDeliverableModal(true)}
                  >
                    Add
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {deliverables.length === 0 ? (
                <div className="p-6 text-center text-slate-500 dark:text-slate-400">
                  No deliverables yet.
                </div>
              ) : (
                <ul className="divide-y divide-slate-200 dark:divide-slate-700">
                  {deliverables.map((deliverable) => (
                    <li
                      key={deliverable.id}
                      className="flex items-center gap-4 p-4"
                    >
                      <button
                        onClick={() =>
                          isAdmin && handleToggleDeliverable(deliverable)
                        }
                        className={`flex-shrink-0 ${
                          isAdmin ? 'cursor-pointer' : 'cursor-default'
                        }`}
                        disabled={!isAdmin}
                      >
                        {deliverable.is_complete ? (
                          <CheckCircle2 className="w-5 h-5 text-amber" />
                        ) : (
                          <Circle className="w-5 h-5 text-slate-300 dark:text-slate-600" />
                        )}
                      </button>
                      <div className="flex-1 min-w-0">
                        <p
                          className={`font-medium ${
                            deliverable.is_complete
                              ? 'text-slate-400 dark:text-slate-500 line-through'
                              : 'text-obsidian dark:text-signal'
                          }`}
                        >
                          {deliverable.title}
                        </p>
                      </div>
                      {deliverable.file_url && (
                        <a
                          href={deliverable.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-shrink-0 p-2 text-slate-400 hover:text-amber dark:hover:text-amber"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - Updates */}
        <div>
          <Card variant="bordered" padding="none">
            <CardHeader className="p-6 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <CardTitle>Updates</CardTitle>
                {isAdmin && (
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<Plus className="w-4 h-4" />}
                    onClick={() => setShowUpdateModal(true)}
                  >
                    Add
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {updates.length === 0 ? (
                <div className="p-6 text-center text-slate-500 dark:text-slate-400">
                  No updates yet.
                </div>
              ) : (
                <ul className="divide-y divide-slate-200 dark:divide-slate-700 max-h-96 overflow-y-auto">
                  {updates.map((update) => (
                    <li key={update.id} className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-amber/10 dark:bg-amber/20 flex items-center justify-center flex-shrink-0">
                          <FileText className="w-4 h-4 text-amber" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-700 dark:text-slate-300">
                            {update.content}
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <Clock className="w-3 h-3 text-slate-400" />
                            <span className="text-xs text-slate-500 dark:text-slate-400">
                              {format(
                                new Date(update.created_at),
                                'MMM d, yyyy h:mm a'
                              )}
                            </span>
                          </div>
                          {isAdmin && !update.is_visible_to_client && (
                            <Badge variant="warning" size="sm" className="mt-2">
                              Internal Only
                            </Badge>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Update Status Modal */}
      <Modal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        title="Update Project Status"
      >
        <Select
          label="Status"
          value={newStatus}
          onChange={(e) => setNewStatus(e.target.value as ProjectStatus)}
          options={statusOptions}
        />
        <ModalActions>
          <Button variant="outline" onClick={() => setShowStatusModal(false)}>
            Cancel
          </Button>
          <Button onClick={handleUpdateStatus} isLoading={isSaving}>
            Update Status
          </Button>
        </ModalActions>
      </Modal>

      {/* Add Update Modal */}
      <Modal
        isOpen={showUpdateModal}
        onClose={() => setShowUpdateModal(false)}
        title="Add Project Update"
      >
        <div className="space-y-4">
          <Textarea
            label="Update"
            value={newUpdate.content}
            onChange={(e) =>
              setNewUpdate({ ...newUpdate, content: e.target.value })
            }
            placeholder="What's new with this project?"
            rows={4}
          />
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={newUpdate.is_visible_to_client}
              onChange={(e) =>
                setNewUpdate({
                  ...newUpdate,
                  is_visible_to_client: e.target.checked,
                })
              }
              className="rounded border-slate-300 text-amber focus:ring-amber"
            />
            <span className="text-sm text-slate-700 dark:text-slate-300">
              Visible to client
            </span>
          </label>
        </div>
        <ModalActions>
          <Button variant="outline" onClick={() => setShowUpdateModal(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleAddUpdate}
            isLoading={isSaving}
            disabled={!newUpdate.content}
          >
            Add Update
          </Button>
        </ModalActions>
      </Modal>

      {/* Add Deliverable Modal */}
      <Modal
        isOpen={showDeliverableModal}
        onClose={() => setShowDeliverableModal(false)}
        title="Add Deliverable"
      >
        <div className="space-y-4">
          <Input
            label="Title"
            value={newDeliverable.title}
            onChange={(e) =>
              setNewDeliverable({ ...newDeliverable, title: e.target.value })
            }
            placeholder="e.g., Final report document"
          />
          <Input
            label="File URL (optional)"
            value={newDeliverable.file_url}
            onChange={(e) =>
              setNewDeliverable({ ...newDeliverable, file_url: e.target.value })
            }
            placeholder="https://..."
            hint="Link to Google Drive, Dropbox, or other file storage"
          />
        </div>
        <ModalActions>
          <Button
            variant="outline"
            onClick={() => setShowDeliverableModal(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddDeliverable}
            isLoading={isSaving}
            disabled={!newDeliverable.title}
          >
            Add Deliverable
          </Button>
        </ModalActions>
      </Modal>
    </div>
  );
}

export default ProjectDetailPage;
